import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/lib/site-config";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "tn-admin-token";

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin/leads";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = `Admin Login · ${SITE.brand.name}`;
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Bitte Benutzername und Passwort eingeben.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/login`,
        { username: username.trim(), password },
        { timeout: 15000 }
      );
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Erfolgreich angemeldet.");
      navigate(from, { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Login fehlgeschlagen. Bitte erneut versuchen.";
      toast.error(typeof detail === "string" ? detail : "Login fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] flex items-center justify-center px-4"
      data-testid="admin-login-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-6 transition-colors"
        >
          <ArrowLeft size={12} /> Zur Website
        </button>

        <div className="flex justify-center mb-8">
          <Logo size={72} showWordmark={false} />
        </div>

        <div className="glass-strong rounded-2xl p-7 sm:p-9">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={14} className="text-[#E8E5D0]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0]">
              Admin Bereich
            </span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-2">
            Login
          </h1>
          <p className="text-sm text-[#C9C6B4] mb-6">
            Zugriff nur für autorisierte Administratoren.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#C9C6B4] mb-1.5 block">
                Benutzername
              </label>
              <input
                type="text"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-[#4E5A3B]/60 border border-white/15 hover:border-white/25 focus:border-[#E8E5D0] focus:ring-2 focus:ring-[#E8E5D0]/25 outline-none rounded-lg px-4 py-3 text-[#F3F1E8] text-[15px] placeholder:text-[#A8A58F] transition-colors"
                placeholder=""
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#C9C6B4] mb-1.5 block">
                Passwort
              </label>
              <input
                type="password"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#4E5A3B]/60 border border-white/15 hover:border-white/25 focus:border-[#E8E5D0] focus:ring-2 focus:ring-[#E8E5D0]/25 outline-none rounded-lg px-4 py-3 text-[#F3F1E8] text-[15px] placeholder:text-[#A8A58F] transition-colors"
                placeholder=""
                data-testid="admin-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] disabled:opacity-50 text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3.5 flex items-center justify-center gap-2 transition-all"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Anmelden…
                </>
              ) : (
                <>
                  <LogIn size={16} /> Einloggen
                </>
              )}
            </button>
          </form>

          <button
            type="button"
            onClick={() => navigate("/admin/forgot")}
            className="mt-4 text-xs font-bold text-[#C9C6B4] hover:text-[#F3F1E8] underline underline-offset-4 transition-colors"
            data-testid="forgot-password-link"
          >
            Passwort vergessen?
          </button>
        </div>
        <p className="text-[11px] text-center text-[#A8A58F] mt-4 tracking-wider">
          Zugriff geschützt · Alle Versuche werden protokolliert
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
